const EXPIRATION_FIELDS = ['expiresIn', 'expires_at', 'expiresAt'];

export const getToken = (user) => user?.accessToken || user?.token || user?.jwt || null;

const decodeBase64 = (value) => {
    if (typeof atob === 'function') return atob(value);
    // eslint-disable-next-line no-undef
    if (typeof Buffer !== 'undefined') return Buffer.from(value, 'base64').toString('binary');
    return null;
};

const decodeJwtPayload = (token) => {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;

        const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
        const paddedPayload = normalizedPayload.padEnd(
            normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
            '='
        );
        const decodedPayload = decodeBase64(paddedPayload);
        if (!decodedPayload) return null;

        return JSON.parse(decodedPayload);
    } catch {
        return null;
    }
};

const getJwtExpirationTime = (token) => {
    const exp = decodeJwtPayload(token)?.exp;
    return Number.isFinite(exp) ? exp * 1000 : null;
};

export const getExpirationTime = (user) => {
    const expirationField = EXPIRATION_FIELDS.find((field) => user?.[field]);
    const expiresIn = expirationField ? user[expirationField] : null;

    if (expiresIn) {
        const numericExpiration = Number(expiresIn);
        if (!Number.isNaN(numericExpiration)) {
            return numericExpiration < 10000000000 ? Date.now() + numericExpiration * 1000 : numericExpiration;
        }

        const parsed = Date.parse(expiresIn);
        if (!Number.isNaN(parsed)) return parsed;
    }

    return getJwtExpirationTime(getToken(user));
};

export const normalizeSession = (user) => {
    if (!user || typeof user !== 'object') return null;

    const token = getToken(user);
    const expirationTime = getExpirationTime(user);

    return {
        ...user,
        accessToken: token,
        expiresIn: expirationTime,
    };
};

export const isValidSession = (user) => {
    if (!user || typeof user !== 'object') return false;

    const token = getToken(user);
    if (!token) return false;

    const expirationTime = getExpirationTime(user);
    return Boolean(expirationTime && expirationTime > Date.now());
};

export const isExpiredSession = (user) => !isValidSession(user);

/**
 * Tenant da assinatura em que a pessoa está, tirado do token (BBF-181).
 *
 * USER só passa no identity com o cabeçalho `tenantId` de uma licença dele, e o login não devolve
 * o tenant. O token traz os tenants das licenças do sistema do login. Com mais de um, não há como
 * escolher aqui, e nada é mandado. ADMIN e SUPPORT são a equipe Busifarm e seguem sem tenant.
 */
export const getTenantIdFromToken = (token) => {
    const payload = token ? decodeJwtPayload(token) : null;
    if (!payload) return null;

    if (['ADMIN', 'SUPPORT'].includes(payload.userType)) return null;

    const tenants = Array.isArray(payload.allowedTenantIds) ? payload.allowedTenantIds : [];
    return tenants.length === 1 ? tenants[0] : null;
};

/** Tenant da sessão: o guardado no login, se houver, senão o do token. */
export const getSessionTenantId = (user) => (
    user?.tenantId
    || user?.tenant_id
    || user?.tenant?.id
    || user?.context?.tenantId
    || getTenantIdFromToken(getToken(user))
);
